/**
 * Pick the batch's SOURCE folder — the bodies and their roster sheet
 * (ADR-035 §3.2). The DOM half of `⇪ Package all…`.
 *
 * `showDirectoryPicker` in `readwrite` mode, ONLY: the root's files (a shelf
 * of bodies is flat; the sheet sits beside them) and the handle, kept so the
 * FILLED sheet and the report are written back where the sheet lives — the
 * id and the clip a run derived must not drift from the folder they
 * describe. There is no file-input fallback: a batch WRITES folders and the
 * sheet, and a browser without the directory picker can do neither, so
 * advertising a readable half would be a door that opens onto nothing
 * (Codex P2, #162 round three). The refusal names the browsers that can.
 * Taken at the GESTURE (the #140 rule): the picker is asked before any work.
 *
 * DOM only, no engine. Depends on: library-read (FolderFileLike),
 * package-folder (DirectoryHandleLike), bake-folder-picker (fileLike).
 * Depended on by: main.
 */
import { fileLike } from './bake-folder-picker.js';
import type { FolderFileLike } from './library-read.js';
import type { DirectoryHandleLike } from './package-folder.js';

interface DirHandle extends DirectoryHandleLike {
  values(): AsyncIterable<{ kind: 'file' | 'directory'; name: string; getFile?: () => Promise<File> }>;
}
interface PickerWindow {
  showDirectoryPicker?: (options?: { mode?: 'read' | 'readwrite'; id?: string }) => Promise<DirHandle>;
}

export interface BatchSource {
  readonly name: string;
  readonly files: readonly FolderFileLike[];
  /** Writable: the filled sheet and the report go back here. */
  readonly dir: DirectoryHandleLike;
}

/** Can this browser run a batch at all? Asked BEFORE the gesture is spent, so the refusal is the first thing said. */
export function batchPickerAvailable(): boolean {
  return (window as PickerWindow).showDirectoryPicker !== undefined;
}

/** null = cancelled (a cancel is not a folder — the caller must not report one). Throws where no directory picker exists. */
export async function pickBatchSource(): Promise<BatchSource | null> {
  const picker = (window as PickerWindow).showDirectoryPicker;
  if (picker === undefined) {
    throw new Error('Package all needs a browser with a directory picker (Chrome or Edge): a batch writes folders and the filled sheet back, and this browser can do neither');
  }
  let dir: DirHandle;
  try {
    dir = await picker({ mode: 'readwrite', id: 'harry-batch-source' });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return null;
    throw err;
  }
  const files: FolderFileLike[] = [];
  for await (const entry of dir.values()) {
    if (entry.kind === 'file' && entry.getFile !== undefined) files.push(fileLike(await entry.getFile(), `${dir.name}/${entry.name}`));
  }
  return { name: dir.name, files, dir };
}
